
import { useState } from 'react'
import { Button } from '../base/Button'

interface Avatar {
  id: string
  name: string
  gender: 'male' | 'female'
  imageUrl: string
  category: string
}

interface AvatarSelectorProps {
  selectedAvatar?: string
  onAvatarSelect: (avatarId: string) => void
  onClose?: () => void
}

export const AvatarSelector = ({ selectedAvatar, onAvatarSelect, onClose }: AvatarSelectorProps) => {
  const [activeGender, setActiveGender] = useState<'male' | 'female'>('male')
  const [activeCategory, setActiveCategory] = useState('casual')

  // Pre-built metaverse style avatars
  const avatars: Avatar[] = [
    // Male Casual
    {
      id: 'male-casual-1',
      name: 'Alex',
      gender: 'male',
      category: 'casual',
      imageUrl: 'https://public.readdy.ai/ai/img_res/06f4031f0f5fb5f73ea895ec13b96f2c.jpg'
    },
    {
      id: 'male-casual-2',
      name: 'Ryan',
      gender: 'male',
      category: 'casual',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20casual%20t-shirt%20and%20shorts%2C%20sneakers%2C%20relaxed%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=2&orientation=squarish'
    },
    {
      id: 'male-casual-3',
      name: 'Jake',
      gender: 'male',
      category: 'casual',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20casual%20jacket%20and%20pants%2C%20modern%20hairstyle%2C%20confident%20expression%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=3&orientation=squarish'
    },
    // Male Professional
    {
      id: 'male-professional-1',
      name: 'Marcus',
      gender: 'male',
      category: 'professional',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20in%20business%20suit%20and%20tie%2C%20professional%20appearance%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=4&orientation=squarish'
    },
    {
      id: 'male-professional-2',
      name: 'David',
      gender: 'male',
      category: 'professional',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20in%20smart%20casual%20blazer%20and%20shirt%2C%20professional%20yet%20approachable%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=5&orientation=squarish'
    },
    // Male Creative
    {
      id: 'male-creative-1',
      name: 'Leo',
      gender: 'male',
      category: 'creative',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20artistic%20clothing%2C%20creative%20accessories%2C%20unique%20hairstyle%2C%20expressive%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=6&orientation=squarish'
    },
    {
      id: 'male-creative-2',
      name: 'Zane',
      gender: 'male',
      category: 'creative',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20male%20avatar%20with%20trendy%20streetwear%2C%20cap%20and%20graphic%20tee%2C%20modern%20urban%20style%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=7&orientation=squarish'
    },
    // Female Casual
    {
      id: 'female-casual-1',
      name: 'Emma',
      gender: 'female',
      category: 'casual',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20casual%20sweater%20and%20jeans%2C%20friendly%20smile%2C%20long%20hair%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=8&orientation=squarish'
    },
    {
      id: 'female-casual-2',
      name: 'Sophia',
      gender: 'female',
      category: 'casual',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20casual%20dress%20and%20sneakers%2C%20relaxed%20pose%2C%20medium%20length%20hair%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=9&orientation=squarish'
    },
    {
      id: 'female-casual-3',
      name: 'Mia',
      gender: 'female',
      category: 'casual',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20casual%20top%20and%20skirt%2C%20comfortable%20style%2C%20wavy%20hair%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=10&orientation=squarish'
    },
    // Female Professional
    {
      id: 'female-professional-1',
      name: 'Victoria',
      gender: 'female',
      category: 'professional',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20in%20business%20suit%20and%20blouse%2C%20professional%20appearance%2C%20neat%20hairstyle%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=11&orientation=squarish'
    },
    {
      id: 'female-professional-2',
      name: 'Grace',
      gender: 'female',
      category: 'professional',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20in%20smart%20casual%20blazer%20and%20dress%2C%20professional%20yet%20elegant%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=12&orientation=squarish'
    },
    // Female Creative
    {
      id: 'female-creative-1',
      name: 'Luna',
      gender: 'female',
      category: 'creative',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20artistic%20bohemian%20clothing%2C%20creative%20accessories%2C%20unique%20hairstyle%2C%20expressive%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=13&orientation=squarish'
    },
    {
      id: 'female-creative-2',
      name: 'Aria',
      gender: 'female',
      category: 'creative',
      imageUrl: 'https://readdy.ai/api/search-image?query=3D%20metaverse%20style%20female%20avatar%20with%20trendy%20streetwear%2C%20fashionable%20outfit%2C%20modern%20hairstyle%2C%20confident%20pose%2C%20clean%20simple%20background%2C%20digital%20art%20style%2C%20high%20quality%20rendering&width=200&height=200&seq=14&orientation=squarish'
    }
  ]

  const categories = [
    { id: 'casual', name: 'Casual', icon: 'ri-t-shirt-line' },
    { id: 'professional', name: 'Professional', icon: 'ri-briefcase-line' },
    { id: 'creative', name: 'Creative', icon: 'ri-palette-line' }
  ]

  const filteredAvatars = avatars.filter(
    avatar => avatar.gender === activeGender && avatar.category === activeCategory
  )

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Choose Your Avatar</h2>
          <p className="text-sm text-gray-600">Select a metaverse style avatar that represents you</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        )}
      </div>

      {/* Gender Selection */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveGender('male')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeGender === 'male'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className="ri-men-line mr-2"></i>
            Male
          </button>
          <button
            onClick={() => setActiveGender('female')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeGender === 'female'
                ? 'bg-white text-purple-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <i className="ri-women-line mr-2"></i>
            Female
          </button>
        </div>
      </div>

      {/* Category Selection */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex space-x-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === category.id
                  ? 'bg-purple-100 text-purple-700 border border-purple-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <i className={`${category.icon} mr-2`}></i>
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Avatar Grid */}
      <div className="p-6 overflow-y-auto max-h-96">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredAvatars.map((avatar) => (
            <div
              key={avatar.id}
              onClick={() => onAvatarSelect(avatar.id)}
              className={`relative group cursor-pointer rounded-lg overflow-hidden transition-all duration-200 ${
                selectedAvatar === avatar.id
                  ? 'ring-2 ring-purple-500 ring-offset-2 transform scale-105'
                  : 'hover:ring-2 hover:ring-purple-300 hover:ring-offset-1 hover:transform hover:scale-102'
              }`}
            >
              <div className="aspect-square bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
                <img
                  src={avatar.imageUrl}
                  alt={avatar.name}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              
              {/* Avatar Name */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <p className="text-white text-sm font-medium text-center">{avatar.name}</p>
              </div>

              {/* Selection Indicator */}
              {selectedAvatar === avatar.id && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
              )}

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            </div>
          ))}
        </div>

        {filteredAvatars.length === 0 && (
          <div className="text-center py-8">
            <i className="ri-user-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No avatars available in this category</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {selectedAvatar ? (
            <>
              Selected: <span className="font-medium">
                {avatars.find(a => a.id === selectedAvatar)?.name}
              </span>
            </>
          ) : (
            'Select an avatar to continue'
          )}
        </div>
        <div className="flex space-x-3">
          {onClose && (
            <Button
              variant="outline"
              onClick={onClose}
              className="whitespace-nowrap cursor-pointer"
            >
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            disabled={!selectedAvatar}
            onClick={() => selectedAvatar && onAvatarSelect(selectedAvatar)}
            className="whitespace-nowrap cursor-pointer"
          >
            Use This Avatar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AvatarSelector
